import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Settings, 
  Save, 
  Upload,
  X,
  Globe,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Github,
  Eye,
  EyeOff,
  Shield,
  Palette,
  Bell
} from 'lucide-react';

interface SiteSettings {
  id: string;
  siteName: string;
  siteDescription: string;
  siteUrl: string;
  adminEmail: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  logo?: string;
  favicon?: string;
  socialLinks: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    github?: string;
  };
  seoSettings: {
    metaTitle: string;
    metaDescription: string;
    metaKeywords: string;
    googleAnalyticsId?: string;
    googleTagManagerId?: string;
  };
  emailSettings: {
    smtpHost?: string;
    smtpPort?: number;
    smtpUser?: string;
    smtpPassword?: string;
    smtpSecure: boolean;
    fromEmail: string;
    fromName: string;
  };
  maintenanceMode: boolean;
  allowRegistration: boolean;
  requireEmailVerification: boolean;
}

interface SettingsFormData {
  siteName: string;
  siteDescription: string;
  siteUrl: string;
  adminEmail: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  facebook: string;
  twitter: string;
  instagram: string;
  linkedin: string;
  github: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  googleAnalyticsId: string;
  googleTagManagerId: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  fromEmail: string;
  fromName: string;
  maintenanceMode: boolean;
  allowRegistration: boolean;
  requireEmailVerification: boolean;
}

const settingsSchema = yup.object({
  siteName: yup
    .string()
    .required('Site name is required')
    .min(2, 'Site name must be at least 2 characters')
    .max(100, 'Site name cannot exceed 100 characters'),
  
  siteDescription: yup
    .string()
    .required('Site description is required')
    .max(500, 'Description cannot exceed 500 characters'),
  
  siteUrl: yup
    .string()
    .required('Site URL is required')
    .url('Must be a valid URL'),
  
  adminEmail: yup
    .string()
    .required('Admin email is required')
    .email('Must be a valid email'),
  
  contactEmail: yup
    .string()
    .required('Contact email is required')
    .email('Must be a valid email'),
  
  contactPhone: yup
    .string()
    .optional(),
  
  contactAddress: yup
    .string()
    .optional(),
  
  facebook: yup.string().optional(),
  twitter: yup.string().optional(),
  instagram: yup.string().optional(),
  linkedin: yup.string().optional(),
  github: yup.string().optional(),
  
  metaTitle: yup
    .string()
    .required('Meta title is required')
    .max(60, 'Meta title should not exceed 60 characters'),
  
  metaDescription: yup
    .string()
    .required('Meta description is required')
    .max(160, 'Meta description should not exceed 160 characters'),
  
  metaKeywords: yup
    .string()
    .optional(),
  
  googleAnalyticsId: yup.string().optional(),
  googleTagManagerId: yup.string().optional(),
  
  smtpHost: yup.string().optional(),
  smtpPort: yup.number().optional(),
  smtpUser: yup.string().optional(),
  smtpPassword: yup.string().optional(),
  smtpSecure: yup.boolean(),
  
  fromEmail: yup
    .string()
    .required('From email is required')
    .email('Must be a valid email'),
  
  fromName: yup
    .string()
    .required('From name is required'),
  
  maintenanceMode: yup.boolean(),
  allowRegistration: yup.boolean(),
  requireEmailVerification: yup.boolean()
});

const SettingsManagement: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'general' | 'contact' | 'social' | 'seo' | 'email' | 'security'>('general');
  const [showPassword, setShowPassword] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(settingsSchema)
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const result = await response.json();
      // Backend returns { success: true, data: {...settings} }
      const settingsData = result.data;
      
      if (settingsData) {
        setSettings(settingsData);
        setLogoPreview(settingsData.logo || null);
        setFaviconPreview(settingsData.favicon || null);
        
        // Populate form with existing settings
        reset({
          siteName: settingsData.siteName || '',
          siteDescription: settingsData.siteDescription || '',
          siteUrl: settingsData.siteUrl || '',
          adminEmail: settingsData.adminEmail || '',
          contactEmail: settingsData.contactEmail || '',
          contactPhone: settingsData.contactPhone || '',
          contactAddress: settingsData.contactAddress || '',
          facebook: settingsData.socialLinks?.facebook || '',
          twitter: settingsData.socialLinks?.twitter || '',
          instagram: settingsData.socialLinks?.instagram || '',
          linkedin: settingsData.socialLinks?.linkedin || '',
          github: settingsData.socialLinks?.github || '',
          metaTitle: settingsData.seoSettings?.metaTitle || '',
          metaDescription: settingsData.seoSettings?.metaDescription || '',
          metaKeywords: settingsData.seoSettings?.metaKeywords || '',
          googleAnalyticsId: settingsData.seoSettings?.googleAnalyticsId || '',
          googleTagManagerId: settingsData.seoSettings?.googleTagManagerId || '',
          smtpHost: settingsData.emailSettings?.smtpHost || '',
          smtpPort: settingsData.emailSettings?.smtpPort || 587,
          smtpUser: settingsData.emailSettings?.smtpUser || '',
          smtpPassword: settingsData.emailSettings?.smtpPassword || '',
          smtpSecure: settingsData.emailSettings?.smtpSecure || false,
          fromEmail: settingsData.emailSettings?.fromEmail || '',
          fromName: settingsData.emailSettings?.fromName || '',
          maintenanceMode: settingsData.maintenanceMode || false,
          allowRegistration: settingsData.allowRegistration || true,
          requireEmailVerification: settingsData.requireEmailVerification || false
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Set default values for demo
      const defaultSettings: SiteSettings = {
        id: '1',
        siteName: 'Web Design Studio',
        siteDescription: 'Professional web design and development services',
        siteUrl: 'https://yourwebsite.com',
        adminEmail: 'admin@yourwebsite.com',
        contactEmail: 'contact@yourwebsite.com',
        contactPhone: '+1 (555) 123-4567',
        contactAddress: '123 Business St, City, State 12345',
        socialLinks: {
          facebook: 'https://facebook.com/yourpage',
          twitter: 'https://twitter.com/yourhandle',
          instagram: 'https://instagram.com/yourprofile'
        },
        seoSettings: {
          metaTitle: 'Web Design Studio - Professional Web Development',
          metaDescription: 'Transform your business with our professional web design and development services. Custom websites that drive results.',
          metaKeywords: 'web design, web development, responsive design, SEO'
        },
        emailSettings: {
          smtpHost: 'smtp.gmail.com',
          smtpPort: 587,
          smtpSecure: true,
          fromEmail: 'noreply@yourwebsite.com',
          fromName: 'Web Design Studio'
        },
        maintenanceMode: false,
        allowRegistration: false,
        requireEmailVerification: true
      };
      
      setSettings(defaultSettings);
      reset({
        siteName: defaultSettings.siteName,
        siteDescription: defaultSettings.siteDescription,
        siteUrl: defaultSettings.siteUrl,
        adminEmail: defaultSettings.adminEmail,
        contactEmail: defaultSettings.contactEmail,
        contactPhone: defaultSettings.contactPhone,
        contactAddress: defaultSettings.contactAddress,
        facebook: defaultSettings.socialLinks?.facebook || '',
        twitter: defaultSettings.socialLinks?.twitter || '',
        instagram: defaultSettings.socialLinks?.instagram || '',
        linkedin: defaultSettings.socialLinks?.linkedin || '',
        github: defaultSettings.socialLinks?.github || '',
        metaTitle: defaultSettings.seoSettings?.metaTitle || '',
        metaDescription: defaultSettings.seoSettings?.metaDescription || '',
        metaKeywords: defaultSettings.seoSettings?.metaKeywords || '',
        googleAnalyticsId: defaultSettings.seoSettings?.googleAnalyticsId || '',
        googleTagManagerId: defaultSettings.seoSettings?.googleTagManagerId || '',
        smtpHost: defaultSettings.emailSettings?.smtpHost || '',
        smtpPort: defaultSettings.emailSettings?.smtpPort || 587,
        smtpUser: defaultSettings.emailSettings?.smtpUser || '',
        smtpPassword: defaultSettings.emailSettings?.smtpPassword || '',
        smtpSecure: defaultSettings.emailSettings?.smtpSecure || false,
        fromEmail: defaultSettings.emailSettings?.fromEmail || '',
        fromName: defaultSettings.emailSettings?.fromName || '',
        maintenanceMode: defaultSettings.maintenanceMode,
        allowRegistration: defaultSettings.allowRegistration,
        requireEmailVerification: defaultSettings.requireEmailVerification
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Structure the data to match the settings format
      const settingsData = {
        siteName: data.siteName,
        siteDescription: data.siteDescription,
        siteUrl: data.siteUrl,
        adminEmail: data.adminEmail,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        contactAddress: data.contactAddress,
        socialLinks: {
          facebook: data.facebook,
          twitter: data.twitter,
          instagram: data.instagram,
          linkedin: data.linkedin,
          github: data.github
        },
        seoSettings: {
          metaTitle: data.metaTitle,
          metaDescription: data.metaDescription,
          metaKeywords: data.metaKeywords,
          googleAnalyticsId: data.googleAnalyticsId,
          googleTagManagerId: data.googleTagManagerId
        },
        emailSettings: {
          smtpHost: data.smtpHost,
          smtpPort: data.smtpPort,
          smtpUser: data.smtpUser,
          smtpPassword: data.smtpPassword,
          smtpSecure: data.smtpSecure,
          fromEmail: data.fromEmail,
          fromName: data.fromName
        },
        maintenanceMode: data.maintenanceMode,
        allowRegistration: data.allowRegistration,
        requireEmailVerification: data.requireEmailVerification
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settingsData),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      alert('Settings saved successfully!');
      fetchSettings();

    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
  };

  const handleFileUpload = async (file: File, type: 'logo' | 'favicon') => {
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', file);  // Changed from 'file' to 'image' to match backend
      formData.append('folder', 'site-assets');  // Store in site-assets folder

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${type}`);
      }

      const result = await response.json();
      console.log('Upload result:', result);
      
      if (type === 'logo') {
        setLogoPreview(result.data.url);
      } else {
        setFaviconPreview(result.data.url);
      }

      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
      fetchSettings();

    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      alert(`Failed to upload ${type}`);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Globe },
    { id: 'contact', name: 'Contact', icon: Mail },
    { id: 'social', name: 'Social Media', icon: Facebook },
    { id: 'seo', name: 'SEO', icon: Eye },
    { id: 'email', name: 'Email', icon: Mail },
    { id: 'security', name: 'Security', icon: Shield }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-gray-600">Configure your website settings and preferences</p>
        </div>
        <button
          onClick={handleSubmit(onSubmit)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Tab Navigation */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Settings Form */}
        <div className="lg:col-span-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              
              {/* General Settings */}
              {activeTab === 'general' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Name *
                      </label>
                      <input
                        {...register('siteName')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your Website Name"
                      />
                      {errors.siteName && (
                        <p className="mt-1 text-sm text-red-600">{errors.siteName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site URL *
                      </label>
                      <input
                        {...register('siteUrl')}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://yourwebsite.com"
                      />
                      {errors.siteUrl && (
                        <p className="mt-1 text-sm text-red-600">{errors.siteUrl.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Description *
                    </label>
                    <textarea
                      {...register('siteDescription')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Brief description of your website..."
                    />
                    {errors.siteDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.siteDescription.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Logo
                      </label>
                      <div className="flex items-center space-x-4">
                        {logoPreview && (
                          <img src={logoPreview} alt="Logo" className="h-12 w-12 object-contain" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'logo')}
                          className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Favicon
                      </label>
                      <div className="flex items-center space-x-4">
                        {faviconPreview && (
                          <img src={faviconPreview} alt="Favicon" className="h-8 w-8 object-contain" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'favicon')}
                          className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Settings */}
              {activeTab === 'contact' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="inline h-4 w-4 mr-1" />
                        Contact Email *
                      </label>
                      <input
                        {...register('contactEmail')}
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="contact@yourwebsite.com"
                      />
                      {errors.contactEmail && (
                        <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="inline h-4 w-4 mr-1" />
                        Contact Phone
                      </label>
                      <input
                        {...register('contactPhone')}
                        type="tel"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Address
                    </label>
                    <textarea
                      {...register('contactAddress')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="123 Business St, City, State 12345"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Email *
                    </label>
                    <input
                      {...register('adminEmail')}
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="admin@yourwebsite.com"
                    />
                    {errors.adminEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.adminEmail.message}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Social Media Settings */}
              {activeTab === 'social' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Social Media Links</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Facebook className="inline h-4 w-4 mr-1" />
                        Facebook
                      </label>
                      <input
                        {...register('facebook')}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://facebook.com/yourpage"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Twitter className="inline h-4 w-4 mr-1" />
                        Twitter
                      </label>
                      <input
                        {...register('twitter')}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://twitter.com/yourhandle"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Instagram className="inline h-4 w-4 mr-1" />
                        Instagram
                      </label>
                      <input
                        {...register('instagram')}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://instagram.com/yourprofile"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Linkedin className="inline h-4 w-4 mr-1" />
                        LinkedIn
                      </label>
                      <input
                        {...register('linkedin')}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://linkedin.com/company/yourcompany"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Github className="inline h-4 w-4 mr-1" />
                        GitHub
                      </label>
                      <input
                        {...register('github')}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://github.com/yourusername"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SEO Settings */}
              {activeTab === 'seo' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Title *
                    </label>
                    <input
                      {...register('metaTitle')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your Site Title - Best Description"
                      maxLength={60}
                    />
                    {errors.metaTitle && (
                      <p className="mt-1 text-sm text-red-600">{errors.metaTitle.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">Recommended: 50-60 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Description *
                    </label>
                    <textarea
                      {...register('metaDescription')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Compelling description of your website that will appear in search results..."
                      maxLength={160}
                    />
                    {errors.metaDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.metaDescription.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">Recommended: 150-160 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Keywords
                    </label>
                    <input
                      {...register('metaKeywords')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="web design, development, SEO, responsive"
                    />
                    <p className="mt-1 text-sm text-gray-500">Comma-separated keywords</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Google Analytics ID
                      </label>
                      <input
                        {...register('googleAnalyticsId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="G-XXXXXXXXXX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Google Tag Manager ID
                      </label>
                      <input
                        {...register('googleTagManagerId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="GTM-XXXXXXX"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Email Settings */}
              {activeTab === 'email' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Email *
                      </label>
                      <input
                        {...register('fromEmail')}
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="noreply@yourwebsite.com"
                      />
                      {errors.fromEmail && (
                        <p className="mt-1 text-sm text-red-600">{errors.fromEmail.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Name *
                      </label>
                      <input
                        {...register('fromName')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your Website Name"
                      />
                      {errors.fromName && (
                        <p className="mt-1 text-sm text-red-600">{errors.fromName.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">SMTP Configuration (Optional)</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SMTP Host
                        </label>
                        <input
                          {...register('smtpHost')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="smtp.gmail.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Port
                        </label>
                        <input
                          {...register('smtpPort', { valueAsNumber: true })}
                          type="number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="587"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SMTP Username
                        </label>
                        <input
                          {...register('smtpUser')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="your-email@gmail.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SMTP Password
                        </label>
                        <div className="relative">
                          <input
                            {...register('smtpPassword')}
                            type={showPassword ? 'text' : 'password'}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="your-app-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <input
                        {...register('smtpSecure')}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        Use SSL/TLS
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === 'security' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Security & Access</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Maintenance Mode</h4>
                        <p className="text-sm text-gray-600">Temporarily disable site access for visitors</p>
                      </div>
                      <input
                        {...register('maintenanceMode')}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Allow Registration</h4>
                        <p className="text-sm text-gray-600">Allow new users to register accounts</p>
                      </div>
                      <input
                        {...register('allowRegistration')}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Require Email Verification</h4>
                        <p className="text-sm text-gray-600">Users must verify their email before accessing the site</p>
                      </div>
                      <input
                        {...register('requireEmailVerification')}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Save className="h-5 w-5 mr-2" />
                Save All Settings
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;